function normalize(text: string): string {
  return text.toLowerCase().replace(/[^a-z0-9]/g, '');
}

/**
 * Match score of `query` against `text`, or `null` when it does not match.
 * Case, spaces and punctuation are ignored. A contiguous match always outranks
 * a scattered one; among scattered matches, longer consecutive runs and a
 * tighter span rank higher.
 */
export function fuzzyScore(text: string, query: string): number | null {
  const needle = normalize(query);
  if (!needle) return 0;
  const haystack = normalize(text);

  const substringIndex = haystack.indexOf(needle);
  if (substringIndex >= 0) return 1_000_000 - substringIndex;

  let position = 0;
  let firstMatch = -1;
  let previousMatch = -2;
  let consecutive = 0;
  for (const char of needle) {
    const matchIndex = haystack.indexOf(char, position);
    if (matchIndex < 0) return null;
    if (firstMatch < 0) firstMatch = matchIndex;
    if (matchIndex === previousMatch + 1) consecutive += 1;
    previousMatch = matchIndex;
    position = matchIndex + 1;
  }
  return consecutive * 1_000 - (previousMatch - firstMatch);
}

/**
 * Items whose `getText` fuzzily matches `query`, best match first. Ties keep
 * their original order; an empty query returns `items` unchanged.
 */
export function fuzzyFilter<T>(items: T[], query: string, getText: (item: T) => string): T[] {
  if (!normalize(query)) return items;
  return items
    .map((item, index) => ({ item, index, score: fuzzyScore(getText(item), query) }))
    .filter((entry): entry is { item: T; index: number; score: number } => entry.score !== null)
    .sort((a, b) => b.score - a.score || a.index - b.index)
    .map((entry) => entry.item);
}
